import type Lenis from 'lenis'
import { useCallback, useSyncExternalStore } from 'react'
import { useNavigate, useViewTransitionState } from 'react-router'
import { arrivedFromAd, isPaidSession } from '@/lib/attribution'
import { readStorage, writeStorage } from '@/lib/storage'

interface Passage {
  id: number
  open: boolean
}

interface CurtainState {
  opening: boolean
  covered: boolean
  passage: Passage | null
}

const CURTAIN_KEY = 'ab.curtain'
const OPENING_FALLBACK = 2600
const PASSAGE_MIN_MS = 150
const PASSAGE_CEILING_MS = 900

const listeners = new Set<() => void>()
const sharedListeners = new Set<() => void>()
let state: CurtainState | null = null
let releaseTimer: number | undefined
let passages = 0
let smoothScroll: Lenis | null = null
let sharedName: string | null = null

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function wait(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms))
}

function opensWithCurtain() {
  if (prefersReducedMotion()) return false
  if (window.location.pathname.replace(/\/+$/, '') !== '') return false
  if (arrivedFromAd()) return false
  return !readStorage<boolean>(CURTAIN_KEY, false, 'session')
}

function scheduleRelease(delay: number) {
  window.clearTimeout(releaseTimer)
  releaseTimer = window.setTimeout(releaseCurtain, delay)
}

function snapshot(): CurtainState {
  if (!state) {
    const opening = opensWithCurtain()
    state = { opening, covered: opening, passage: null }
  }
  return state
}

function update(next: Partial<CurtainState>) {
  state = { ...snapshot(), ...next }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  if (snapshot().covered && releaseTimer === undefined) scheduleRelease(OPENING_FALLBACK)
  return () => {
    listeners.delete(listener)
  }
}

export function useCurtainState() {
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}

export function useCurtainSettled() {
  return !useCurtainState().covered
}

export function markCurtainSeen() {
  writeStorage(CURTAIN_KEY, true, 'session')
  if (snapshot().opening) update({ opening: false })
}

export function releaseCurtain() {
  window.clearTimeout(releaseTimer)
  if (snapshot().covered) update({ covered: false })
}

export function endPassage(id: number) {
  if (snapshot().passage?.id === id) update({ passage: null })
}

function startPassage(ready: Promise<unknown>) {
  passages += 1
  const id = passages
  update({ covered: true, passage: { id, open: false } })
  const settled = Promise.race([ready.catch(() => undefined), wait(PASSAGE_CEILING_MS)])
  void Promise.all([settled, wait(PASSAGE_MIN_MS)]).then(() => {
    if (snapshot().passage?.id !== id) return
    update({ covered: false, passage: { id, open: true } })
  })
}

export function registerSmoothScroll(instance: Lenis) {
  smoothScroll = instance
  return () => {
    if (smoothScroll === instance) smoothScroll = null
  }
}

export function stopScrollInertia() {
  if (!smoothScroll || smoothScroll.isStopped) return
  smoothScroll.stop()
  smoothScroll.start()
}

function subscribeShared(listener: () => void) {
  sharedListeners.add(listener)
  return () => {
    sharedListeners.delete(listener)
  }
}

function shareElement(name: string | null) {
  if (sharedName === name) return
  sharedName = name
  for (const listener of sharedListeners) listener()
}

export function useSharedTransitionName(name: string, path: string) {
  const transitioning = useViewTransitionState(path)
  const shared = useSyncExternalStore(
    subscribeShared,
    () => sharedName,
    () => null,
  )
  return transitioning && shared === name ? name : undefined
}

export function transitionOrigin(event: {
  clientX: number
  clientY: number
  detail: number
  currentTarget: Element
}) {
  if (event.detail > 0) return { x: event.clientX, y: event.clientY }
  const rect = event.currentTarget.getBoundingClientRect()
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}

export function useTransitionNavigate() {
  const navigate = useNavigate()
  return useCallback(
    (to: string, origin?: { x: number; y: number }, shared?: string) => {
      stopScrollInertia()
      shareElement(shared ?? null)
      if (prefersReducedMotion()) {
        void navigate(to)
        return
      }
      if ('startViewTransition' in document) {
        const root = document.documentElement
        root.style.setProperty('--vt-x', origin ? `${Math.round(origin.x)}px` : '50%')
        root.style.setProperty('--vt-y', origin ? `${Math.round(origin.y)}px` : '50%')
        void navigate(to, { viewTransition: true, flushSync: true })
        return
      }
      if (isPaidSession()) {
        void navigate(to)
        return
      }
      startPassage(Promise.resolve(navigate(to)))
    },
    [navigate],
  )
}
